import './InitiativeScopeTable.scss';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getClosestSelectionTable } from '@lib/popups';
import { getInitiativeScopeTableColumns } from './InitiativeScopeTableColumns';
import { InitiativeScopeTableActionBar } from './InitiativeScopeTableActionBar';
import isEqual from 'lodash/isEqual';
import { keyPaginationDefaultQueryParams } from '@ui/KeyPagination';
import { coreActions } from '@sonar-software/sonar-core';
import { paginationDefaultQueryParams, paginationFallbackQueryParams, PeriscopePagination } from '@periscopes/general';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { SelectionTable } from '@ui/tables';
import { useAction, useOnMount, usePardotEnabled, usePrevious, useQueryParams, useSelectionTable, useSelectionTableWithInversion, useSonarSelector } from '@hooks';
function getRowKey(row) {
    return row.sonarObjectId;
}
const selectors = createAsyncSelectors((state) => state.core.initiatives.sonarObjects.get);
export const InitiativeScopeTable = ({ count, dynamicNameFilter, initiativeId, nameFilter, selectNamespace }) => {
    const scrollableListRef = useRef(null);
    const showPardotTypes = usePardotEnabled();
    const resource = useSonarSelector(selectors.data);
    const columns = useMemo(() => getInitiativeScopeTableColumns(showPardotTypes), [showPardotTypes]);
    const previousNameFilter = usePrevious(nameFilter, nameFilter);
    const previousCount = usePrevious(count, count);
    const loadScopes = useAction(coreActions.requestGetCoreInitiativesSonarObjects);
    const defaultParams = Object.assign(Object.assign({}, paginationDefaultQueryParams), { count: 5, initiativeId, objectNameSearchText: nameFilter, objectTypes: [], sortBy: 'name', sortDirection: 'descending' });
    const selectionData = useSelectionTableWithInversion({
        getRowKey: getRowKey,
        results: resource === null || resource === void 0 ? void 0 : resource.results,
        totalCount: resource === null || resource === void 0 ? void 0 : resource.totalCount
    });
    const selectionDataRef = useRef(null);
    const { queryParams: dynamicQueryParams } = useQueryParams({
        defaultParams: Object.assign(Object.assign({}, keyPaginationDefaultQueryParams), { count: 5, filters: dynamicNameFilter, ordering: '' })
    });
    const { queryParams, updateParams } = useQueryParams({
        defaultParams,
        fallbackParams: paginationFallbackQueryParams,
        onUpdate: (newParams, prev, combined) => {
            const didObjectTypesChange = newParams.objectTypes && !isEqual(newParams.objectTypes, prev === null || prev === void 0 ? void 0 : prev.objectTypes);
            loadScopes(combined);
            if (didObjectTypesChange) {
                selectionData.resetSelection();
            }
        }
    });
    useOnMount(() => {
        loadScopes(defaultParams);
    });
    useEffect(() => {
        if (previousNameFilter !== nameFilter) {
            updateParams({
                objectNameSearchText: nameFilter
            });
        }
    }, [nameFilter, previousNameFilter, updateParams]);
    selectionDataRef.current = useSelectionTable({
        getRowKey,
        namespace: selectNamespace,
        queryParams: dynamicQueryParams,
        resourceIdsUri: `v3/initiatives/${initiativeId}/scope/bulk`,
        results: resource === null || resource === void 0 ? void 0 : resource.results
    });
    const handleOnTableChange = useCallback((pagination, filters, sorter) => {
        var _a;
        updateParams({
            objectTypes: (_a = filters.type) !== null && _a !== void 0 ? _a : [],
            sortBy: sorter.columnKey,
            sortDirection: `${sorter.order || 'descend'}ing`
        });
    }, [updateParams]);
    useEffect(() => {
        if (previousCount !== count) {
            loadScopes(queryParams);
        }
    }, [loadScopes, previousCount, queryParams, count]);
    return (React.createElement(React.Fragment, null,
        React.createElement(SelectionTable, { actionBar: React.createElement(InitiativeScopeTableActionBar, { initiativeId: initiativeId, selectNamespace: selectNamespace }), className: 'initiative-scope-table data-dictionary-table', columns: columns, dataSource: resource === null || resource === void 0 ? void 0 : resource.results, getPopupContainer: getClosestSelectionTable, isActionBarAbove: false, onChange: handleOnTableChange, ref: scrollableListRef, rowKey: getRowKey, selectionData: selectionDataRef.current, selectionType: COPY_SPECIFIC.SCOPES }),
        React.createElement(PeriscopePagination, { loading: false, onChange: updateParams, queryParams: queryParams, scrollRef: scrollableListRef, totalCount: Number(resource === null || resource === void 0 ? void 0 : resource.totalCount) })));
};
